<template>
  <div class="tasks-container">
    <div class="tasks-header">
      <h1>Tasks</h1>
      <div class="filter-controls">
        <select v-model="groupingOption" class="group-select">
          <option value="date">Group by Due Date</option>
          <option value="person">Group by Person</option>
        </select>
        <select v-model="statusFilter" class="status-select">
          <option value="open">Open Tasks</option>
          <option value="completed">Completed Tasks</option>
          <option value="all">All Tasks</option>
        </select>
      </div>
      <button class="add-task-btn" @click="showAddTaskModal">
        <i class="fas fa-plus desktop-only"></i>
        <span>Add Task</span>
      </button>
    </div>

    <div v-if="loading" class="loading">Loading tasks...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <div v-if="groupingOption === 'date'" class="task-groups">
        <div v-for="group in tasksByDate" :key="group.label" class="task-group">
          <div class="group-header" @click="toggleGroup(group)">
            <span class="disclosure-triangle" :class="{ open: !group.collapsed }">▶</span>
            <h2>{{ group.label }}</h2>
            <span class="task-count">({{ group.tasks.length }})</span>
          </div>
          <div v-if="!group.collapsed" class="task-list">
            <div class="task-list-header">
              <div class="task-checkbox-header"></div>
              <div class="task-title-header">Task</div>
              <div class="task-assignee-header">Related To</div>
              <div class="task-priority-header">Priority</div>
              <div class="task-due-header">Due Date</div>
            </div>
            <div v-for="task in group.tasks" :key="task.id" class="task-item">
              <div class="task-checkbox" @click.stop>
                <input type="checkbox" 
                       :checked="task.status === 'completed'"
                       @change="toggleTaskComplete(task)"
                       :id="'task-' + task.id"
                       class="animated-checkbox">
                <div class="fireworks-container"></div>
                <div class="checkmark-path"></div>
              </div>
              <div class="task-main" @click.stop="openEditModal(task)">
                <div class="task-title" :class="{ completed: task.status === 'completed' }">
                  {{ task.title }}
                  <div class="task-description" v-if="task.description">
                    {{ task.description }}
                  </div>
                </div>
              </div>
              <div v-if="groupingOption !== 'person'" class="task-assignee">
                {{ task.related_to_user_name || 'Unknown User' }}
                <!-- Mobile-only date display -->
                <div class="mobile-due-date">
                  {{ formatDate(task.due_date) }}
                </div>
              </div>
              <div class="task-priority">
                <span class="priority-badge" :class="task.priority.toLowerCase()">
                  {{ task.priority }}
                </span>
              </div>
              <div class="task-due-date">
                {{ formatDate(task.due_date) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="task-groups">
        <div v-for="group in tasksByPerson" :key="group.label" class="task-group">
          <div class="group-header" @click="toggleGroup(group)">
            <span class="disclosure-triangle" :class="{ open: !group.collapsed }">▶</span>
            <h2>{{ group.label }}</h2>
            <span class="task-count">({{ group.tasks.length }})</span>
          </div>
          <div v-if="!group.collapsed" class="task-list">
            <div class="task-list-header" :class="{ 'person-view': groupingOption === 'person' }">
              <div class="task-checkbox-header"></div>
              <div class="task-title-header">Task</div>
              <div v-if="groupingOption !== 'person'" class="task-assignee-header">Related To</div>
              <div class="task-priority-header">Priority</div>
              <div class="task-due-header">Due Date</div>
            </div>
            <div v-for="task in group.tasks" :key="task.id" class="task-item" :class="{ 'person-view': groupingOption === 'person' }">
              <div class="task-checkbox" @click.stop>
                <input type="checkbox" 
                       :checked="task.status === 'completed'"
                       @change="toggleTaskComplete(task)"
                       :id="'task-' + task.id"
                       class="animated-checkbox">
                <div class="fireworks-container"></div>
                <div class="checkmark-path"></div>
              </div>
              <div class="task-main" @click.stop="openEditModal(task)">
                <div class="task-title" :class="{ completed: task.status === 'completed' }">
                  {{ task.title }}
                  <div class="task-description" v-if="task.description">
                    {{ task.description }}
                  </div>
                </div>
              </div>
              <div v-if="groupingOption !== 'person'" class="task-assignee">
                {{ task.related_to_user_name || 'Unknown User' }}
              </div>
              <!-- Mobile-only date display -->
              <div class="mobile-due-date">
                {{ formatDate(task.due_date) }}
              </div>
              <div class="task-priority">
                <span class="priority-badge" :class="task.priority.toLowerCase()">
                  {{ task.priority }}
                </span>
              </div>
              <div class="task-due-date">
                {{ formatDate(task.due_date) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TaskEditModal 
      v-if="showEditModal"
      :show="showEditModal"
      :task="selectedTask"
      :userId="currentUserId"
      @close="closeEditModal"
      @task-updated="handleTaskUpdated"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import confetti from 'canvas-confetti';
import { taskService } from '../services/taskService';
import TaskEditModal from '../components/tasks/TaskEditModal.vue';

export default defineComponent({
  name: 'TasksView',
  components: {
    TaskEditModal
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const tasks = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const selectedTask = ref(null);
    const groupingOption = ref('date');
    const statusFilter = ref('open');
    const showEditModal = ref(false);
    const collapsedGroups = ref(new Set());
    const currentUserId = ref(null);

    // Watch for changes in route params
    watch(() => route.params.id, async (newId) => {
      if (newId) {
        try {
          loading.value = true;
          const response = await taskService.getTask(newId);
          selectedTask.value = response.task;
          showEditModal.value = true;
        } catch (err) {
          console.error('Error loading task:', err);
          error.value = 'Failed to load task details';
          router.push('/tasks');
        } finally {
          loading.value = false;
        }
      }
    }, { immediate: true });

    const loadTasks = async () => {
      try {
        loading.value = true;
        const response = await taskService.getTasksForUser();
        tasks.value = response.tasks;
        // Set the current user ID from the first task's assigned_to
        // This works because getTasksForUser only returns tasks assigned to the current user
        if (response.tasks && response.tasks.length > 0) {
          currentUserId.value = response.tasks[0].assigned_to;
        }
      } catch (err) {
        error.value = 'Failed to load tasks';
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadTasks();
    });

    const toggleTaskComplete = async (task) => {
      try {
        const newStatus = task.status === 'completed' ? 'open' : 'completed';
        const checkbox = document.getElementById('task-' + task.id);
        
        if (newStatus === 'completed' && checkbox) {
          const taskElement = checkbox.closest('.task-item');
          if (taskElement) {
            // Get position for the confetti
            const rect = checkbox.getBoundingClientRect();
            const x = (rect.left + rect.width/2) / window.innerWidth;
            const y = (rect.top + rect.height/2) / window.innerHeight;
            
            // Fire mini star confetti
            confetti({
              particleCount: 20,
              spread: 40,
              origin: { x, y },
              colors: ['#FFD700', '#FFC107', '#FFE082'], // Gold colors
              shapes: ['star'],
              ticks: 100,
              scalar: 0.8,
              gravity: 0.3,
              drift: 1, // Drift to the right
              angle: 60, // Angle upward and to the right
              startVelocity: 20, // Slightly faster
              disableForReducedMotion: true
            });
            
            // Wait a bit then fade out
            await new Promise(resolve => setTimeout(resolve, 600));
            taskElement.style.transition = 'opacity 0.8s';
            taskElement.style.opacity = '0';
            await new Promise(resolve => setTimeout(resolve, 800));
          }
        }

        // Update in backend
        await taskService.updateTask(task.id, {
          ...task,
          status: newStatus,
          due_date: task.due_date,
          assigned_to: task.assigned_to
        });
        
        // Now refresh the list
        await loadTasks();
      } catch (error) {
        console.error('Error updating task status:', error);
      }
    };

    const getPersonName = (task) => {
      return task.related_to_user_name || '';
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'No due date';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Reset time components for comparison
      today.setHours(0, 0, 0, 0);
      tomorrow.setHours(0, 0, 0, 0);
      const taskDate = new Date(date);
      taskDate.setHours(0, 0, 0, 0);
      
      if (taskDate.getTime() === today.getTime()) {
        return 'Today';
      } else if (taskDate.getTime() === tomorrow.getTime()) {
        return 'Tomorrow';
      }
      
      // Format date based on year
      const options = { 
        month: 'short', 
        day: 'numeric',
        year: taskDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      };
      return taskDate.toLocaleDateString('en-AU', options);
    };

    const groupTasksByDate = (tasksList) => {
      if (!tasksList || !tasksList.length) {
        return [];
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const groups = [
        {
          label: 'Overdue',
          tasks: [],
          collapsed: collapsedGroups.value.has(`${groupingOption.value}-Overdue`)
        },
        {
          label: 'Today',
          tasks: [],
          collapsed: collapsedGroups.value.has(`${groupingOption.value}-Today`)
        },
        {
          label: 'Tomorrow',
          tasks: [],
          collapsed: collapsedGroups.value.has(`${groupingOption.value}-Tomorrow`)
        },
        {
          label: 'This Week',
          tasks: [],
          collapsed: collapsedGroups.value.has(`${groupingOption.value}-This Week`)
        },
        {
          label: 'Later',
          tasks: [],
          collapsed: collapsedGroups.value.has(`${groupingOption.value}-Later`)
        }
      ];

      tasksList.forEach(task => {
        if (!task.due_date) {
          groups[4].tasks.push(task); // Put tasks with no due date in "Later"
          return;
        }

        const dueDate = new Date(task.due_date);
        if (isNaN(dueDate.getTime())) {
          console.error(`Invalid due date for task "${task.title}":`, task.due_date);
          groups[4].tasks.push(task); // Put tasks with invalid dates in "Later"
          return;
        }

        // Reset hours to compare dates properly
        const taskDate = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
        
        if (taskDate < today) {
          groups[0].tasks.push(task);
        } else if (taskDate.getTime() === today.getTime()) {
          groups[1].tasks.push(task);
        } else if (taskDate.getTime() === tomorrow.getTime()) {
          groups[2].tasks.push(task);
        } else if (taskDate <= nextWeek) {
          groups[3].tasks.push(task);
        } else {
          groups[4].tasks.push(task);
        }
      });

      // Sort tasks within each group by priority and then by due date
      groups.forEach(group => {
        group.tasks.sort((a, b) => {
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          const priorityDiff = priorityOrder[a.priority.toLowerCase()] - priorityOrder[b.priority.toLowerCase()];
          if (priorityDiff !== 0) return priorityDiff;
          
          // Handle cases where due_date might be null or invalid
          const dateA = new Date(a.due_date || 0);
          const dateB = new Date(b.due_date || 0);
          return dateA - dateB;
        });
      });

      const filteredGroups = groups.filter(group => group.tasks.length > 0);
      return filteredGroups;
    };

    const groupTasksByPerson = (tasksList) => {
      if (!tasksList || !tasksList.length) {
        return [];
      }

      const groups = new Map();
      
      // Add "No one" group first
      groups.set('No one', {
        label: 'General Tasks',
        tasks: [],
        collapsed: collapsedGroups.value.has(`${groupingOption.value}-General Tasks`)
      });
      
      tasksList.forEach(task => {
        const personName = task.related_to_user_name || 'Unknown User';
        
        if (!groups.has(personName)) {
          groups.set(personName, {
            label: personName,
            tasks: [],
            collapsed: collapsedGroups.value.has(`${groupingOption.value}-${personName}`)
          });
        }
        
        groups.get(personName).tasks.push(task);
      });

      // Sort tasks within each group by priority and due date
      groups.forEach(group => {
        group.tasks.sort((a, b) => {
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          const priorityDiff = priorityOrder[a.priority.toLowerCase()] - priorityOrder[b.priority.toLowerCase()];
          if (priorityDiff !== 0) return priorityDiff;
          return new Date(a.due_date) - new Date(b.due_date);
        });
      });

      // Convert to array and filter out empty groups
      const groupedTasks = Array.from(groups.values())
        .filter(group => group.tasks.length > 0)
        .sort(a => a.label === 'General Tasks' ? 1 : -1);
      
      return groupedTasks;
    };

    const filteredTasks = computed(() => {
      if (statusFilter.value === 'all') return tasks.value;
      return tasks.value.filter(task => {
        if (statusFilter.value === 'open') {
          return task.status !== 'completed';
        } else {
          return task.status === 'completed';
        }
      });
    });

    const tasksByDate = computed(() => groupTasksByDate(filteredTasks.value));
    const tasksByPerson = computed(() => groupTasksByPerson(filteredTasks.value));

    const toggleGroup = (group) => {
      group.collapsed = !group.collapsed;
      if (group.collapsed) {
        collapsedGroups.value.add(`${groupingOption.value}-${group.label}`);
      } else {
        collapsedGroups.value.delete(`${groupingOption.value}-${group.label}`);
      }
    };

    const openEditModal = async (task) => {
      if (task.id) {
        try {
          loading.value = true;
          const response = await taskService.getTask(task.id);
          selectedTask.value = response.task;
          showEditModal.value = true;
          // Update URL to reflect selected task
          router.push(`/tasks/${task.id}`);
        } catch (err) {
          console.error('Error loading task:', err);
          error.value = 'Failed to load task details';
        } finally {
          loading.value = false;
        }
      }
    };

    const closeEditModal = () => {
      selectedTask.value = null;
      showEditModal.value = false;
      // If we're on a specific task route, go back to tasks list
      if (route.params.id) {
        router.push('/tasks');
      }
    };

    const handleTaskUpdated = async () => {
      await loadTasks();
      closeEditModal();
    };

    const showAddTaskModal = () => {
      selectedTask.value = {
        title: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        due_date: new Date().toISOString().split('T')[0],
        related_to_user_id: null,
        assigned_to: currentUserId.value,
        id: null
      };
      showEditModal.value = true;
    };

    return {
      loading,
      error,
      tasks,
      groupingOption,
      statusFilter,
      tasksByDate,
      tasksByPerson,
      toggleTaskComplete,
      getPersonName,
      formatDate,
      toggleGroup,
      showEditModal,
      selectedTask,
      openEditModal,
      closeEditModal,
      loadTasks,
      handleTaskUpdated,
      showAddTaskModal,
      currentUserId
    };
  }
});
</script>

<style scoped>
.tasks-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  overflow-x: hidden;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.tasks-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 0 16px;
}

.tasks-header h1 {
  margin: 0;
  font-size: 24px;
  flex: 1;
}

.filter-controls {
  display: flex;
  gap: 12px;
  margin: 0 24px;
  flex: 2;
  justify-content: center;
}

.group-select,
.status-select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 140px;
}

.add-task-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.add-task-btn:hover {
  background-color: #45a049;
}

.desktop-only {
  display: inline-block;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }

  .tasks-header {
    padding: 0 12px;
  }

  .filter-controls {
    flex-direction: column;
    margin: 16px 12px;
    gap: 8px;
  }

  .group-select,
  .status-select {
    width: 100%;
    min-width: unset;
  }

  .add-task-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
}

.task-list-header {
  display: grid;
  grid-template-columns: 30px 1fr 150px 100px 100px;
  gap: 16px;
  padding: 8px 16px;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}

.task-list-header.person-view {
  grid-template-columns: 30px 1fr 100px 100px;
}

.task-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  transition: opacity 0.8s ease-out;
  overflow: hidden; /* Prevent scrolling within rows */
}

.mobile-due-date {
  display: none;
}

.task-groups {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.task-group {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.group-header:hover {
  background-color: #f0f1f2;
}

.disclosure-triangle {
  font-size: 12px;
  margin-right: 8px;
  transition: transform 0.2s ease;
}

.disclosure-triangle.open {
  transform: rotate(90deg);
}

.group-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.task-count {
  margin-left: 8px;
  color: #666;
  font-size: 14px;
}

.task-list {
  padding: 8px 16px;
  overflow: hidden;
}

.task-checkbox-header {
  width: 20px;
}

.task-title-header {
  flex: 1;
  font-weight: 600;
  text-align: left;
  padding-right: 16px;
}

.task-assignee-header {
  width: 150px;
  font-weight: 600;
  text-align: left;
  padding-right: 16px;
}

.task-priority-header {
  width: 100px;
  font-weight: 600;
  text-align: left;
  padding-right: 16px;
}

.task-due-header {
  width: 120px;
  font-weight: 600;
  text-align: left;
}

.task-checkbox {
  position: relative;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.animated-checkbox {
  position: absolute;
  width: 20px;
  height: 20px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid #ccc;
  border-radius: 4px;
  background: white;
  transition: all 0.3s ease-out;
}

.animated-checkbox:checked {
  border-color: #4CAF50;
  background-color: #4CAF50;
}

.animated-checkbox:checked + .fireworks-container + .checkmark-path::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  animation: checkmark 0.3s ease-out forwards;
  z-index: 2;
}

@keyframes checkmark {
  0% {
    height: 0;
    width: 0;
    opacity: 0;
  }
  40% {
    height: 0;
    width: 5px;
    opacity: 1;
  }
  100% {
    height: 10px;
    width: 5px;
    opacity: 1;
  }
}

.fireworks-container {
  position: absolute;
  width: 100px;
  height: 100px;
  pointer-events: none;
  z-index: 1;
  left: -30px;
  top: -30px;
}

.firework-particle {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: linear-gradient(to right, #4CAF50, #81C784);
  transform-origin: center;
  animation: firework 1s ease-out forwards;
  animation-delay: var(--delay, 0s);
  box-shadow: 0 0 4px #4CAF50;
}

@keyframes firework {
  0% {
    transform: rotate(var(--angle)) translateY(0) scale(1);
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: rotate(var(--angle)) translateY(40px) scale(0);
    opacity: 0;
  }
}

.task-main {
  flex: 1;
  text-align: left;
  padding-right: 16px;
  min-width: 0; /* Allow text to wrap */
  overflow: hidden; /* Prevent scrolling */
}

.task-title {
  display: block;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 4px;
  cursor: pointer;
  text-align: left;
  word-break: break-word; /* Allow long words to break */
  white-space: normal; /* Allow text to wrap */
}

.task-description {
  color: #666;
  font-size: 13px;
  text-align: left;
  word-break: break-word;
  white-space: normal;
}

.task-assignee {
  width: 150px;
  font-size: 13px;
  color: #666;
  text-align: left;
  padding-right: 16px;
}

.task-related {
  font-size: 12px;
  color: #999;
  text-align: left;
  margin-top: 4px;
}

.task-priority {
  width: 100px;
  font-size: 13px;
  color: #666;
  text-align: left;
  padding-right: 16px;
}

.task-due {
  width: 120px;
  font-size: 13px;
  color: #666;
  text-align: left;
}

.priority-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.priority-badge.high {
  background-color: #ffebee;
  color: #d32f2f;
}

.priority-badge.medium {
  background-color: #fff3e0;
  color: #f57c00;
}

.priority-badge.low {
  background-color: #e8f5e9;
  color: #388e3c;
}

.loading {
  text-align: center;
  padding: 20px;
  color: #666;
}

.error {
  text-align: center;
  padding: 20px;
  color: #d32f2f;
  background-color: #ffebee;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .tasks-container {
    padding-top: 80px;
    padding-left: 20px;
    padding-right: 20px;
  }

  .task-list-header {
    display: none !important;
  }

  .task-priority,
  .task-due-date {
    display: none !important;
  }

  .task-item.person-view {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr) 70px;
    gap: 4px;
    padding: 12px 8px;
    align-items: start;
    max-width: 100%;
    overflow-x: hidden;
  }

  .task-item.person-view .mobile-due-date {
    display: block;
    font-size: 12px;
    color: #666;
    white-space: nowrap;
    justify-self: end; /* Align to the right */
  }

  .task-checkbox {
    align-self: center;
  }

  .task-main {
    padding-right: 4px;
    min-width: 0;
  }

  .task-title {
    font-size: 14px;
    line-height: 1.4;
    margin-bottom: 4px;
  }

  .task-description {
    font-size: 12px;
    color: #666;
  }

  .task-assignee {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: right;
    max-width: 70px;
    margin-right: 4px;
  }

  .mobile-due-date {
    display: block;
    font-size: 12px;
    color: #666;
    white-space: nowrap;
  }

  .task-assignee {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: right;
  }

  .assignee-label {
    font-weight: 600;
    font-size: 12px;
    color: #333;
  }

  .assignee-value {
    font-size: 12px;
    color: #666;
  }

  .filter-controls {
    flex-direction: column;
    gap: 8px;
  }

  .group-select,
  .status-select {
    width: 100%;
  }
}
</style>
